/**
 * LinkPickerRow — one row of the "Linked records" picker shared between
 * NoteCreateModal (create flow) and MemoComposePanel (compose flow) via
 * useMemoCreate.
 *
 * Two visual states:
 *   1. **Editing** (no association type or no target selected) — full
 *      association-type Select + AssociatedCrmObjectInput pickers.
 *   2. **Collapsed** (association type + targets selected) — compact
 *      entity badges with a trash icon to remove the whole row.
 *      Click the badges to re-expand to editing mode.
 *
 * Stateless beyond its own collapsed/expanded toggle — the parent
 * passes the row's current `LinkedAssociation` value and handlers.
 */

import { Edit05, Trash01 } from '@untitledui/icons';
import { useCallback, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { Select } from '@/lib/base/forms/select/select';
import { AssociatedCrmObjectInput } from '@/lib/crm/components/associations/AssociatedCrmObjectInput';
import { EntityBadgeById } from '@/lib/crm/components/badges/EntityBadgeById';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';

/**
 * State for a single picker row. Parents hold an array of these in their
 * own React state and pass the matching one to LinkPickerRow.
 */
export interface LinkedAssociation {
  /** A unique-per-render id so React reconciles rows correctly when removed. */
  id: string;
  /** Selected association type, or null until the user picks one. */
  associationType: AssociationType | null;
  /** Picked target entity_ids (cardinality may allow more than one). */
  targetEntityIds: string[];
}

interface LinkPickerRowProps {
  link: LinkedAssociation;
  items: { id: string; label: string }[];
  orgId: number;
  onAssociationTypeChange: (id: string, associationTypeName: string) => void;
  onTargetChange: (id: string, value: string[] | null) => void;
  onRemove: (id: string) => void;
  isSubmitting: boolean;
}

export function LinkPickerRow({
  link,
  items,
  orgId,
  onAssociationTypeChange,
  onTargetChange,
  onRemove,
  isSubmitting,
}: LinkPickerRowProps) {
  // Collapse once both type and target are set; user can re-expand to edit.
  const isComplete =
    link.associationType !== null && link.targetEntityIds.length > 0;
  const [isEditing, setIsEditing] = useState(!isComplete);

  const handleSelectionChange = useCallback(
    (key: string | number | null) => {
      if (typeof key === 'string') {
        onAssociationTypeChange(link.id, key);
      }
    },
    [link.id, onAssociationTypeChange]
  );

  const handleTargetChange = useCallback(
    (value: string[] | null) => {
      onTargetChange(link.id, value);
      // Auto-collapse when a target is selected
      if (value && value.length > 0) {
        setIsEditing(false);
      }
    },
    [link.id, onTargetChange]
  );

  const handleRemove = useCallback(
    () => onRemove(link.id),
    [link.id, onRemove]
  );

  const handleExpand = useCallback(() => setIsEditing(true), []);

  // Collapsed: compact badge row
  if (isComplete && !isEditing) {
    return (
      <div className='flex items-center gap-1.5 group'>
        <div className='flex items-center gap-1 flex-1 min-w-0 flex-wrap'>
          {link.targetEntityIds.map(entityId => (
            <EntityBadgeById
              key={entityId}
              entityId={entityId}
              orgId={orgId}
              variant='xs'
            />
          ))}
        </div>
        <Button
          size='sm'
          color='tertiary'
          iconLeading={Edit05}
          className='!size-5 !p-0.5 [&_[data-icon]]:!size-3 opacity-0 group-hover:opacity-100 transition-opacity'
          onClick={handleExpand}
          isDisabled={isSubmitting}
          aria-label='Edit link'
        />
        <Button
          size='sm'
          color='tertiary'
          iconLeading={Trash01}
          className='!size-5 !p-0.5 [&_[data-icon]]:!size-3 opacity-0 group-hover:opacity-100 transition-opacity'
          onClick={handleRemove}
          isDisabled={isSubmitting}
          aria-label='Remove link'
        />
      </div>
    );
  }

  // Editing: full pickers. The link-type Select and the remove button sit on
  // one center-aligned row so the trash lines up with the Select (an
  // `items-start` layout floated it above the control); the target picker
  // drops full-width beneath.
  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center gap-2'>
        <div className='flex-1 min-w-0'>
          <Select
            size='xs'
            placeholder='Choose link type'
            items={items}
            selectedKey={link.associationType?.association_type_name ?? null}
            onSelectionChange={handleSelectionChange}
            isDisabled={isSubmitting || items.length === 0}
            aria-label='Link type'
          >
            {item => <Select.Item id={item.id}>{item.label}</Select.Item>}
          </Select>
        </div>
        <Button
          size='sm'
          color='tertiary'
          iconLeading={Trash01}
          className='!size-6 !p-1 [&_[data-icon]]:!size-3.5 shrink-0'
          onClick={handleRemove}
          isDisabled={isSubmitting}
          aria-label='Remove link'
        />
      </div>
      {link.associationType && (
        <AssociatedCrmObjectInput
          associationType={link.associationType}
          value={link.targetEntityIds}
          onChange={handleTargetChange}
          orgId={orgId}
          disabled={isSubmitting}
          size='xs'
        />
      )}
    </div>
  );
}
